import {useCallback, useEffect, useState} from 'react';
import {apiClient} from '../utils/apiClient';

export interface User {
    id: string;
    username: string;
    email: string;
    role: 'user' | 'admin';
    status: 'activated' | 'archived' | 'disabled';
}

export interface AuthState {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
    message: string | null; // Pour les messages d'information (ex: activation requise)
}

export interface LoginCredentials {
    username: string;
    password: string;
}

export interface RegisterCredentials {
    username: string;
    email: string;
    password: string;
}

export const useAuth = () => {
    const [authState, setAuthState] = useState<AuthState>({
        user: null,
        isAuthenticated: false,
        isLoading: true,
        error: null,
        message: null,
    });

    // Fonction pour charger les informations utilisateur depuis le token
    const loadUserFromToken = async () => {
        try {
            // apiClient.get('/me') tentera un refresh automatique si le token
            // est absent ou expiré (grâce au cookie httpOnly refresh_token)
            const user = await apiClient.get<User>('/me');
            setAuthState({
                user,
                isAuthenticated: true,
                isLoading: false,
                error: null,
                message: null,
            });
        } catch {
            localStorage.removeItem('access_token');
            setAuthState({
                user: null,
                isAuthenticated: false,
                isLoading: false,
                error: null,
                message: null,
            });
        }
    };

    // Vérifier le token au chargement
    useEffect(() => {
        loadUserFromToken();
    }, []); // Pas de dépendance pour éviter les boucles

    // Se déconnecter automatiquement quand une requête retourne 401
    useEffect(() => {
        const handleUnauthorized = () => {
            setAuthState({
                user: null,
                isAuthenticated: false,
                isLoading: false,
                error: null,
                message: 'Votre session a expiré, veuillez vous reconnecter.',
            });
        };
        window.addEventListener('auth:unauthorized', handleUnauthorized);
        return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
    }, []);
    const login = async (credentials: LoginCredentials): Promise<void> => {
        setAuthState(prev => ({...prev, isLoading: true, error: null, message: null}));

        try {
            // Authentification avec l'API
            const formData = new FormData();
            formData.append('username', credentials.username);
            formData.append('password', credentials.password);

            const tokenResponse = await apiClient.postForm<{
                access_token: string;
                token_type: string
            }>('/token', formData);
            localStorage.setItem('access_token', tokenResponse.access_token);

            // Récupérer les informations utilisateur avec le token
            const user = await apiClient.get<User>('/me');

            setAuthState({
                user,
                isAuthenticated: true,
                isLoading: false,
                error: null,
                message: null,
            });
        } catch (error) {
            setAuthState(prev => ({
                ...prev,
                isLoading: false,
                error: error instanceof Error ? error.message : 'Erreur de connexion',
                message: null,
            }));
            throw error;
        }
    };
    const register = async (credentials: RegisterCredentials): Promise<void> => {
        setAuthState(prev => ({...prev, isLoading: true, error: null, message: null}));

        try {
            await apiClient.post('/register', credentials);

            // Ne pas se connecter automatiquement, mais informer l'utilisateur qu'il doit activer son compte
            setAuthState(prev => ({
                ...prev,
                isLoading: false,
                error: null,
                message: 'Inscription réussie ! Veuillez patienter que votre compte soit activé par un administrateur.',
            }));
        } catch (error) {
            setAuthState(prev => ({
                ...prev,
                isLoading: false,
                error: error instanceof Error ? error.message : 'Erreur d\'inscription',
                message: null,
            }));
            throw error;
        }
    };

    const logout = (): void => {
        localStorage.removeItem('access_token');
        // Supprimer le cookie refresh token côté serveur
        fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/token/logout`, {
            method: 'POST',
            credentials: 'include',
        }).catch(() => { /* ignore */ });
        setAuthState({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,
            message: null,
        });
    };

    const clearMessage = useCallback((): void => {
        setAuthState(prev => ({
            ...prev,
            message: null,
        }));
    }, []);

    return {
        ...authState,
        login,
        register,
        logout,
        clearMessage,
    };
};
